import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { saveAs } from "file-saver";
import {
    myAxios,
} from "src/components/common/functions";
import {
    Project
} from "./types";
import { fields } from "./ProjectFilterForm";
import { appendField } from "../common/FilterFrame/FilterField";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { extraFields } from "./ProjectList";

export function buildProjectName(project: Partial<Project>) {

    return [
        project.projectNumber,
        project.organizationName,
        project.clientProjectNumber,
    ].filter(Boolean).join(" - ");
}

export async function projectsLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if(![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)){
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }
    const page  = Number(url.searchParams.get("page"));
    const limit = Number(url.searchParams.get("limit"));
    const formData = new FormData();
    formData.append("page", String(page));
    formData.append("limit", String(limit));
    if(url.searchParams.get("fav") === "1") formData.append("fav", "1");
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({ fileName: "projects/readProjects.php", formData });
    if(!res) throw redirect("..");
    return res.data;
}

export async function projectLoader({ params }: LoaderFunctionArgs) {
    const projectId = params.projectId;
    if (!projectId) {
        throw redirect("/Projects");
    }
    const formData = new FormData();
    formData.append("id", projectId);
    const res = await myAxios({ fileName: "projects/readProject.php", formData });
    if (!res || !res.data) {
        throw redirect("..");
    }
    return res.data;
}

export async function projectAction({ request, params }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    const action = String(formData.get("action") ?? "");
    const urlMap: Record<string, string> = {
        Add: "projects/createProject.php",
        Edit: "projects/updateProject.php",
    };
    const fileName = urlMap[action];
    if (!fileName) {
        window.alert("Invalid Keyword");
        return null;
    }
    
    formData.set("contactIds", JSON.stringify(formData.getAll("contactIds")));

    const res = await myAxios({ fileName, formData, signal: controller.signal });
    if (!res) return null;

    if (action === "Add") {
        throw redirect(`/Projects/${res.data.id}`);
    }
    
    throw redirect(`/Projects/${params.projectId ?? res.data.id}`);
}

export async function deleteProjectAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    const res = await myAxios({
        fileName: "projects/deleteProject.php",
        formData,
        signal: controller.signal,
    });
    if (!res) {
        throw redirect("/Projects");
    }
    throw redirect("/Projects");
}

export async function projectExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());
    const url = new URL(request.url);
    const formData = await request.formData();
    for(const field of fields){
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: `/projects/exportProjects.php`,
        formData, 
        responseType: "blob", 
        signal: controller.signal
    });
    if(!res) throw redirect(`..?${url.searchParams.toString()}`);
    const disposition = res.headers["content-disposition"];
    let filename = "projects.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}
